"use client";

import React, { useState } from "react";
import { motion } from "framer-motion";
import { CircleQuestionMark as HelpCircle, GitBranch as GitBranches, LifeBuoy, FlaskConical, Bug, Lightbulb, CircleCheck as CheckCircle2, Compass, Globe } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
interface BehaviorItem {
  id: string;
  icon: React.ElementType;
  titleVi: string;
  titleEn: string;
  descVi: string;
  descEn: string;
  badgeVi: string;
  badgeEn: string;
}
const instructorBehaviors: BehaviorItem[] = [{
  id: "guiding-questions",
  icon: HelpCircle,
  titleVi: "Đặt câu hỏi gợi mở",
  titleEn: "Asks guiding questions",
  descVi: "Khơi gợi tư duy phản biện bằng những câu hỏi đúng lúc, dẫn dắt học viên tự tìm ra đáp án thay vì thụ động tiếp nhận.",
  descEn: "Prompts critical thinking with timely questions, guiding students to discover answers on their own rather than passive listening.",
  badgeVi: "Phương pháp 01",
  badgeEn: "Method 01"
}, {
  id: "break-down-problem",
  icon: GitBranches,
  titleVi: "Hướng dẫn phân tích vấn đề",
  titleEn: "Helps break down the problem",
  descVi: "Hỗ trợ chia nhỏ bài toán kỹ thuật hoặc lập trình phức tạp thành các bước xử lý logic, từng bước dễ tiếp cận.",
  descEn: "Supports decomposing complex technical or coding challenges into logical, manageable steps.",
  badgeVi: "Phương pháp 02",
  badgeEn: "Method 02"
}, {
  id: "support-struggle",
  icon: LifeBuoy,
  titleVi: "Hỗ trợ khi gặp khó khăn",
  titleEn: "Supports students when they struggle",
  descVi: "Đồng hành và đưa ra gợi ý scaffold đúng thời điểm, đảm bảo học viên duy trì động lực và không bị bế tắc.",
  descEn: "Steps in with scaffolding at key moments to keep students motivated and moving forward smoothly.",
  badgeVi: "Phương pháp 03",
  badgeEn: "Method 03"
}, {
  id: "encourages-experimentation",
  icon: FlaskConical,
  titleVi: "Khuyến khích thử nghiệm",
  titleEn: "Encourages experimentation",
  descVi: "Tạo môi trường an toàn để học viên thử sức với nhiều phương án thiết kế, lập trình và không ngại thất bại.",
  descEn: "Creates a safe environment for trying multiple design and code iterations without fear of initial failure.",
  badgeVi: "Phương pháp 04",
  badgeEn: "Method 04"
}, {
  id: "test-and-debug",
  icon: Bug,
  titleVi: "Hướng dẫn kiểm tra & sửa lỗi",
  titleEn: "Helps students test and debug",
  descVi: "Rèn luyện quy trình kiểm thử hệ thống (test) và sửa lỗi (debug) bài bản, hình thành tư duy kỹ sư chuẩn mực.",
  descEn: "Teaches systematic methods to test hypotheses and resolve bugs in code or mechanical engineering.",
  badgeVi: "Phương pháp 05",
  badgeEn: "Method 05"
}, {
  id: "student-autonomy",
  icon: Lightbulb,
  titleVi: "Tạo điều kiện tự giải quyết",
  titleEn: "Creates room for student autonomy",
  descVi: "Dành khoảng trống độc lập để học viên tự tin làm chủ sản phẩm LEGO/Robotics và khắc sâu kiến thức.",
  descEn: "Gives students full ownership over their projects so they internalize core STEM concepts through direct practice.",
  badgeVi: "Phương pháp 06",
  badgeEn: "Method 06"
}];
export default function TeachingApproach() {
  const [lang, setLang] = useState<"vi" | "en">("vi");
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.06
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: "easeOut"
      }
    }
  };
  return <section id="teachingapproach" data-nav="light" className="relative overflow-hidden bg-background py-20 text-foreground md:py-28">
      {/* Blueprint Grid Decoration */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-[radial-gradient(var(--border)_1px,transparent_1px)] bg-[size:24px_24px] opacity-40" />
      
      {/* Precision Accent Lines */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
      <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-secondary/40 to-transparent" />

      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Top Bar / Blueprint Tag & Language Toggle */}
        <div className="mb-8 flex flex-wrap items-center justify-between gap-4 border-b border-border/80 pb-4">
          <div className="flex items-center gap-2">
            <Compass className="size-5 text-primary" />
            <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
              {lang === "vi" ? "MÔ HÌNH SƯ PHẠM / ROBOTO ACADEMY" : "PEDAGOGICAL FRAMEWORK / ROBOTO ACADEMY"}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <Globe className="size-4 text-muted-foreground" aria-hidden="true" />
            <div className="inline-flex rounded-lg border border-border bg-card p-0.5 shadow-2xs">
              <Button variant={lang === "vi" ? "default" : "ghost"} size="sm" className="h-7 px-2.5 text-xs font-medium" onClick={() => setLang("vi")}>
                Tiếng Việt
              </Button>
              <Button variant={lang === "en" ? "default" : "ghost"} size="sm" className="h-7 px-2.5 text-xs font-medium" onClick={() => setLang("en")}>
                English
              </Button>
            </div>
          </div>
        </div>

        {/* Section Header with Supporting Mentor Photograph */}
        <div className="mb-16 grid grid-cols-1 items-center gap-8 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Badge variant="outline" className="mb-4 border-secondary/50 bg-secondary/10 text-secondary-foreground">
              {lang === "vi" ? "Triết lý giảng dạy" : "Teaching Philosophy"}
            </Badge>
            
            <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl">
              {lang === "vi" ? <>Giáo viên không chỉ đưa ra đáp án.</> : <>Teachers don't just hand out the answers.</>}
            </h2>

            <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
              {lang === "vi" ? "Tại ROBOTO, chúng tôi tin rằng trải nghiệm chủ động giải quyết vấn đề giúp học viên ghi nhớ sâu sắc và phát triển tư duy logic vượt trội." : "At ROBOTO, we believe that active problem-solving builds deeper technical understanding and critical thinking skills."}
            </p>
          </div>

          <div className="relative lg:col-span-5">
            <div className="group relative overflow-hidden rounded-xl border border-border/80 bg-card p-2 shadow-md">
              {/* Technical Blueprint Corner Accents */}
              <div aria-hidden="true" className="absolute top-2 right-2 z-10 size-6 border-t-2 border-r-2 border-secondary" />
              <div aria-hidden="true" className="absolute bottom-2 left-2 z-10 size-6 border-b-2 border-l-2 border-primary/40" />

              <div className="relative aspect-4/3 w-full overflow-hidden rounded-lg">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/mentor_guiding_student_debugging.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/mentor_guiding_student_debugging.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/mentor_guiding_student_debugging.png-wvc-sizes" alt={lang === "vi" ? "Giáo viên ROBOTO hướng dẫn học viên kiểm tra và sửa lỗi lập trình robot" : "ROBOTO instructor guiding student through coding and debugging robotics"} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                {/* Technical Overlay */}
                <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-slate-950/20 to-transparent" />
                <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between text-white">
                  <span className="font-mono text-xs font-medium tracking-wide uppercase text-slate-200">
                    {lang === "vi" ? "HƯỚNG DẪN 1:1 / TROUBLESHOOTING" : "1:1 MENTORSHIP / TROUBLESHOOTING"}
                  </span>
                  <span className="inline-flex items-center rounded-md bg-primary/90 px-2 py-0.5 font-mono text-[10px] text-white">
                    ROBOTO LAB
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Feature Grid */}
        <motion.div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true
      }}>
          {instructorBehaviors.map((item, i) => {
          const Icon = item.icon;
          return <motion.div key={item.id} data-index={i} variants={itemVariants}>
                <Card className="group relative h-full border-border/80 bg-card/90 transition-all duration-200 hover:border-primary/40 hover:shadow-md">
                  <div aria-hidden="true" className="absolute top-0 right-0 h-12 w-12 border-t-2 border-r-2 border-transparent transition-colors duration-200 group-hover:border-primary/30" />
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <div className="flex size-11 items-center justify-center rounded-lg bg-muted text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                      <Icon className="size-5" />
                    </div>
                    <Badge variant="secondary" className="font-mono text-[11px]">
                      {lang === "vi" ? item.badgeVi : item.badgeEn}
                    </Badge>
                  </CardHeader>
                  <CardContent className="pt-2">
                    <CardTitle className="text-lg font-semibold text-foreground">
                      {lang === "vi" ? item.titleVi : item.titleEn}
                    </CardTitle>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                      {lang === "vi" ? item.descVi : item.descEn}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Balanced Pedagogical Model Banner */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.4,
        delay: 0.2
      }} className="mt-12 rounded-xl border border-secondary/30 bg-muted/50 p-6 sm:p-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-secondary text-secondary-foreground">
              <CheckCircle2 className="size-5" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-foreground sm:text-lg">
                {lang === "vi" ? "Mô hình kết hợp: Hướng dẫn có định hướng & Học tập chủ động" : "Balanced Model: Teacher-Guided & Student-Active"}
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                {lang === "vi" ? "Giáo viên tại ROBOTO vẫn trực tiếp truyền đạt lý thuyết cốt lõi và hướng dẫn kỹ thuật nền tảng, sau đó đóng vai trò huấn luyện viên đồng hành để học viên làm chủ kiến thức bằng chính trải nghiệm thực tế." : "Instructors at ROBOTO actively deliver foundational technical concepts and core theory, then pivot to expert mentors who scaffold students as they apply skills through hands-on robotics projects."}
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}